import { Component, h, Element, Event, EventEmitter, State, Prop } from '@stencil/core';

import logoOntrace from '../../../assets/logo-ontrace.svg';
import store from '../../../helpers/store';
import { Cameras } from '../../../helpers/Cameras';
import { Browser, MobileOS } from '../../../models/IDevice';

//import cv from 'opencv.js';
import * as tf from '@tensorflow/tfjs';

import { FlowStatus } from '../../../models/FlowStatus';
import { IdCaptureValuesSide, TranslationValues } from '../../../models/ITranslationValues';
import { Translations } from '../../../helpers/TranslationUtils';
import { addExifInImg } from '../../../helpers/security';
import { LoggerService } from '../../../helpers/LoggerService';
import { delay } from '../../../utils/utils';
import { IdCaptureMode } from '../../../models/IdCaptureModes';
import { DetectionHelpers } from '../../../helpers/Detection';
import { DetectedObject } from '../../../models/IDetectedObject';
import { modelLoadingService } from '../../../helpers/ModelLoadingService';

import loaderImg from '../../../assets/loader.svg';
import icoPhoto from '../../../assets/ico-photo.svg';

enum ImageFormat {
  JPEG = 'image/jpeg',
  PNG = 'image/png',
}

@Component({
  tag: 'id-auto-capture',
  styleUrl: 'id-auto-capture.css',
  shadow: false,
})
export class IdAutoCapture {
  @Element() el: HTMLElement;
  private translations: TranslationValues;
  private videoElement: HTMLVideoElement;
  private canvasElement: HTMLCanvasElement;
  private processingCanvasElement: HTMLCanvasElement;
  // @ts-ignore
  private videoContainer: HTMLDivElement;
  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private animationFrameId: number | null = null;

  private canvasContext: CanvasRenderingContext2D | null = null;
  private processingContext: CanvasRenderingContext2D | null = null;
  private timerInterval: NodeJS.Timeout | null = null;
  private modelMatches: number = 0;
  private opencvMatCheckInterval: NodeJS.Timeout | null = null;
  private detectedWidth: number = 0;
  private detectedHeight: number = 0;
  public static readonly CLASS_NAMES: string[] = ['DL', 'TD1_BACK', 'TD1_FRONT', 'TD2', 'TD3'];
  private idCaptureTranslations: IdCaptureValuesSide;

  @Prop() public captureMode: string;

  @State() mode: IdCaptureMode;
  @State() private titleMesage: string;
  @State() private titleStyle: string;
  @State() recordedChunks: Blob[] = [];
  @State() countdown: number = 0;
  @State() isCapturing: boolean = false;
  @State() detectedArea: number | null = null;
  @State() elapsedTime: number = 0;
  @State() isLoading: boolean = true;
  @State() manualCapturing: boolean = false;

  @State() guideColor: string = 'id-guide-rectangle-white';

  @Event({
    eventName: 'photoIdCapture',
  })
  eventPhotoCapture: EventEmitter;

  @Event({
    eventName: 'recordingIdCapture',
  })
  eventRecordingIdReady: EventEmitter;

  @Event({
    eventName: 'timeElapsed',
  })
  eventTimeElapsed: EventEmitter;

  constructor() {}

  async componentWillLoad() {
    this.translations = await Translations.getValues();
    this.idCaptureTranslations = this.captureMode === 'front' ? this.translations.IdCaptureValuesFront : this.translations.IdCaptureValuesBack;
    this.initVariables();

    await this.waitModelLoad();
  }

  async componentDidLoad() {
    if (!modelLoadingService.isReady()) {
      await modelLoadingService.initialize();
    }

    this.initElements();
    await this.getMedia();
    this.startRecording();
    if (!store.manualCaptureEnabled) {
      this.handleStartDetection();
    }
    this.countdownNotCaptured();
  }

  disconnectedCallback() {
    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
      this.mediaRecorder = null;
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
    }
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    this.detectedArea = null;

    if (this.timerInterval !== null) {
      clearInterval(this.timerInterval); // Ensure the timer is stopped
      this.timerInterval = null;
    }

    if (this.opencvMatCheckInterval !== null) {
      clearInterval(this.opencvMatCheckInterval);
      this.opencvMatCheckInterval = null;
    }

    if (tf) {
      tf.disposeVariables();
      tf.dispose();
    }
  }

  private countdownNotCaptured() {
    this.timerInterval = setInterval(async () => {
      this.elapsedTime++;
      LoggerService.logMulti(`Time open: ${this.elapsedTime} seconds`);

      if (this.elapsedTime >= store.manualCaptureTimeout) {
        if (this.timerInterval !== null) {
          clearInterval(this.timerInterval); // Stop the timer
          this.timerInterval = null;
          LoggerService.warn(`Warning: Component has been open for ${store.manualCaptureTimeout} seconds!`);
        }
        if (this.modelMatches === 0) {
          store.manualCaptureEnabled = true;
        }
        this.eventTimeElapsed.emit(true);
      }
    }, 1000);
  }

  async waitModelLoad() {
    await modelLoadingService.initialize();
  }

  initVariables() {
    LoggerService.logMulti('id-capture | initVariables');
    this.titleMesage = this.translations.IdCaptureValues.Waiting;

    this.titleStyle = 'text-center px-1 mb-2';
  }
  private initElements() {
    this.videoElement = this.el.querySelector('video#video');
    this.canvasElement = this.el.querySelector('canvas#output');
    this.processingCanvasElement = this.el.querySelector('canvas#processingCanvasElement');

    if (this.canvasElement) {
      this.canvasContext = this.canvasElement.getContext('2d', { willReadFrequently: true });
    }
    if (this.processingCanvasElement) {
      this.processingContext = this.processingCanvasElement.getContext('2d', { willReadFrequently: true });
    }
  }

  private checkReadyState() {
    if (modelLoadingService.isReady() && this.mediaStream) {
      this.isLoading = false;
      LoggerService.logMulti('Scanner ready. Click "Start ID Detection" to begin.');
      this.mode = IdCaptureMode.IDLE;
    } else if (modelLoadingService.isReady()) {
      LoggerService.logMulti('AI model loaded. Waiting for camera...');
    }
  }

  private async getMedia() {
    try {
      if (!store.cameraId) {
        await Cameras.InitCameras(store.device);
      }
      const constraints = Cameras.GetConstraints(store.cameraId, store.device);

      this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      if (this.videoElement) {
        this.videoElement.srcObject = this.mediaStream;

        await new Promise<void>(resolve => {
          const onLoadedMetadata = () => {
            this.videoElement.removeEventListener('loadedmetadata', onLoadedMetadata);
            LoggerService.logMulti(`Video dimensions: ${this.videoElement.videoWidth}x${this.videoElement.videoHeight}`);
            if (this.videoElement.videoWidth > 0 && this.videoElement.videoHeight > 0) {
              // const aspectRatio = this.videoElement.videoWidth / this.videoElement.videoHeight;
              // const videoWidth = this.videoContainer.offsetWidth;
              // const videoHeight = videoWidth / aspectRatio;
              // this.videoContainer.style.minHeight = `${videoHeight}px`;
              // LoggerService.logMulti(`Adjusted video container height to maintain aspect ratio: ${videoHeight}px`);
              resolve();
            } else {
              onLoadedMetadata();
            }
          };
          this.videoElement.addEventListener('loadedmetadata', onLoadedMetadata);
        });

        await new Promise<void>(resolve => {
          const onLoadedData = () => {
            this.videoElement.removeEventListener('loadeddata', onLoadedData);
            LoggerService.logMulti('Video data loaded and ready to play.');
            resolve();
          };
          this.videoElement.addEventListener('loadeddata', onLoadedData);
        });

        this.videoElement.play();
        this.isLoading = false;
        if (store.manualCaptureEnabled) {
          this.titleMesage = this.idCaptureTranslations.TitleManualCapture;
        } else {
          this.titleMesage = this.idCaptureTranslations.Title;
        }
      }

      this.checkReadyState();
    } catch (err) {
      LoggerService.error('Error accessing camera:', err);
      LoggerService.logMulti('Get media error:', err);
      this.isLoading = true;
    }
  }

  private handleStartDetection = async () => {
    if (!this.mediaStream) {
      LoggerService.logMulti('Camera not ready. Please allow camera access.');
      return;
    }
    if (!modelLoadingService.isReady()) {
      LoggerService.logMulti('AI model is not loaded yet. Please wait.');
      return;
    }

    this.mode = IdCaptureMode.DETECTING_STRAIGHT_ID;
    LoggerService.logMulti('Starting ID detection...');
    this.recordedChunks = [];
    this.detectedArea = null;
    this.countdown = 0;

    await this.startRecording();
    //await this.waitForVideoDimensions();
    this.animationFrameId = requestAnimationFrame(async () => await this.processVideoFrame());
  };

  private startRecording = async () => {
    if (!store.videoRecording) {
      return;
    }

    if (!this.mediaStream || !this.videoElement) {
      return;
    }

    this.recordedChunks = [];
    if (store.device.os.name === MobileOS.iOS || store.device.os.name === MobileOS.MacOS || store.device.browser.name === Browser.Safari) {
      await delay(500);
    }

    var options = { mimeType: Cameras.webmMimeType.mime, videoBitsPerSecond: 1300000 };

    if (!MediaRecorder.isTypeSupported(options.mimeType)) {
      if (store.device.os.name === MobileOS.iOS || store.device.os.name === MobileOS.MacOS || store.device.browser.name === Browser.Safari)
        options.mimeType = Cameras.mp4MimeType.mime;
    }

    this.mediaRecorder = new MediaRecorder(this.mediaStream, { mimeType: 'video/webm' });

    this.mediaRecorder.ondataavailable = ({ data }) => {
      if (data.size > 0) {
        this.recordedChunks = [...this.recordedChunks, data];
      }
    };

    this.mediaRecorder.onstop = () => {
      if (this.recordedChunks.length > 0) {
        clearInterval(this.timerInterval);
        var rec = new Blob(this.recordedChunks, {
          type: options.mimeType.split(';')[0],
        });
        this.eventRecordingIdReady.emit(rec);
        this.mode = IdCaptureMode.DONE;
      }
    };

    this.mediaRecorder.start();
    LoggerService.logMulti('Recording started...');
  };

  private stopRecording = () => {
    this.isLoading = true;
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
      LoggerService.logMulti('Recording stopped.');
    } else {
      clearInterval(this.timerInterval);
      this.eventRecordingIdReady.emit(null);
      this.mode = IdCaptureMode.DONE;
    }
  };

  private capture = async (): Promise<boolean> => {
    return new Promise(async resolve => {
      if (this.videoElement && this.canvasElement) {
        const video = this.videoElement;
        const canvas = this.canvasElement;
        const context = this.canvasContext;

        const MAX_WIDTH = 1280;
        const MAX_HEIGHT = 720;

        let width = video.videoWidth;
        let height = video.videoHeight;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
            LoggerService.logMulti(`Adjusted video container dimensions to: ${width}x${height}`);
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
            LoggerService.logMulti(`Adjusted video container dimensions to: ${width}x${height}`);
          }
        }

        canvas.width = width;
        canvas.height = height;

        if (!store.manualCaptureEnabled) {
          let detectedObject = await this.getDetection(video);
          if (!detectedObject) {
            LoggerService.logMulti('No object detected after count!');
            this.eventTimeElapsed.emit();
            resolve(false);
          }
        }

        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (frame: Blob) => {
            if (
              frame &&
              frame.type === ImageFormat.JPEG &&
              store.device.os.name !== MobileOS.iOS &&
              store.device.os.name !== MobileOS.MacOS &&
              store.device.browser.name !== Browser.Safari
            ) {
              try {
                LoggerService.logMulti('stream | getFrame | addExifInImg');
                addExifInImg(frame, this.mediaStream.getTracks()[0], { width: this.videoElement.videoWidth, height: this.videoElement.videoHeight }).then(updatedFrame => {
                  this.handleCapture(updatedFrame, resolve);
                });
              } catch (e) {
                this.eventTimeElapsed.emit();
                resolve(false);
              }
            } else {
              LoggerService.logMulti('stream | getFrame | resolve');
              this.handleCapture(frame, resolve);
            }
          },
          ImageFormat.PNG,
          0.9,
        );
        LoggerService.logMulti('Image captured!');
      } else {
        LoggerService.logMulti('Video element or canvas element not found. Cannot capture image.');
      }
    });
  };

  private handleCapture = (frame: Blob, resolve: (value: boolean | PromiseLike<boolean>) => void) => {
    this.isCapturing = true;
    setTimeout(() => {
      this.isCapturing = false;
      this.eventPhotoCapture.emit(frame);
      resolve(true);
    }, 400);
  };

  private manualCapture = () => {
    if (this.manualCapturing) return;

    this.countdown = 3;
    this.manualCapturing = true;
    const timer = setInterval(async () => {
      this.countdown--;
      if (this.countdown <= 0) {
        clearInterval(timer);
        this.stopRecording();
        let captured = await this.capture(); // Take the snapshot
        if (captured) {
          LoggerService.logMulti('ID captured! Click "Start Tilt Detection" to analyze tilt.');
          this.countdown = 0;
          this.detectedArea = null;
          this.mode = IdCaptureMode.DONE;
          this.manualCapturing = false;
        } else {
          this.mode = IdCaptureMode.DETECTING_STRAIGHT_ID;
        }
      }
    }, 1000);
  };

  private handleStartTiltDetection = async () => {
    if (store.flowStatus == FlowStatus.IDBACK) {
      this.stopRecording();
    } else {
      LoggerService.logMulti('Detecting tilt... Please tilt the ID. (Rectangle will appear on detected object).');
      this.mode = IdCaptureMode.DETECTING_TILT_LIVE;
      this.detectedArea = null;
      this.titleMesage = this.translations.HowToValues.TiltTitle;
      this.guideColor = 'id-guide-rectangle-white';
      this.modelMatches = 0;
      await delay(1000);
      this.animationFrameId = requestAnimationFrame(async () => await this.processVideoFrame());
    }
  };

  private async getDetection(video: HTMLVideoElement): Promise<DetectedObject | null> {
    const model = modelLoadingService.getIDModel();
    const detectedObject = await DetectionHelpers.detectIdWithTensorFlow(model, video, 0.9);
    if (!detectedObject) return null;

    const [x, y, width, height] = detectedObject.bbox;
    const aspectRatio = width / height;
    const objectArea = width * height;

    store.idType = detectedObject.class;

    return { class: detectedObject.class, score: detectedObject.score, aspectRatio, objectArea, x, y, width, height };
  }

  private async processVideoFrame() {
    // Only proceed if TF.js model is ready, and in an active detection mode
    if (this.videoElement && this.processingCanvasElement && this.processingContext) {
      const video = this.videoElement;
      const processingCanvas = this.processingCanvasElement;
      const processingContext = this.processingContext;

      // Ensure canvas dimensions match video dimensions
      processingCanvas.width = video.videoWidth;
      processingCanvas.height = video.videoHeight;

      if (video.videoHeight > 0 && video.videoWidth > 0) {
        processingContext.drawImage(video, 0, 0, processingCanvas.width, processingCanvas.height);
        // No need to get ImageData if passing video element directly to model.detect
        // const imageData = processingContext.getImageData(0, 0, processingCanvas.width, processingCanvas.height);

        let detectedObject = await this.getDetection(video); // Pass video element directly

        try {
          // --- Drawing and UI Updates based on mode ---
          if (detectedObject) {
            this.modelMatches++;
            this.guideColor = 'id-guide-rectangle-yellow';
            let displayMessage = `Detected: ${detectedObject.class} (${(detectedObject.score * 100).toFixed(1)}%). Area: ${detectedObject.objectArea.toFixed(0)} px². x: ${
              detectedObject.x
            } y:${detectedObject.y} width: ${detectedObject.width} height: ${detectedObject.height}. Aspect Ratio: ${detectedObject.aspectRatio.toFixed(2)}`;
            LoggerService.logMulti(displayMessage);
            this.detectedArea = detectedObject.objectArea;

            if (this.mode === IdCaptureMode.DETECTING_STRAIGHT_ID) {
              let conditionsPassed = this.applyConditionsForDetection(detectedObject, video);

              // Check for straight ID criteria
              if (conditionsPassed) {
                // Area must be at least 10% of video
                this.guideColor = 'id-guide-rectangle-green';
                LoggerService.logMulti('Straight ID detected! Starting countdown...');
                this.titleMesage = this.translations.IdCaptureValues.IdDetected;
                // Trigger countdown and capture
                this.countdown = 3;
                if (this.animationFrameId !== null) {
                  cancelAnimationFrame(this.animationFrameId);
                  this.animationFrameId = null;
                }
                this.mode = IdCaptureMode.COUNTDOWN_CAPTURE;
                this.detectedWidth = detectedObject.width;
                this.detectedHeight = detectedObject.height;

                const timer = setInterval(async () => {
                  this.countdown--;
                  if (this.countdown <= 0) {
                    clearInterval(timer);
                    let captured = await this.capture(); // Take the snapshot
                    if (captured) {
                      LoggerService.logMulti('ID captured! Click "Start Tilt Detection" to analyze tilt.');
                      this.countdown = 0;
                      this.detectedArea = null;
                      if (store.hasTilt) {
                        this.mode = IdCaptureMode.AWAITING_TILT_START;
                        await this.handleStartTiltDetection();
                      } else {
                        this.mode = IdCaptureMode.DONE;
                        this.stopRecording();
                      }
                      this.guideColor = 'id-guide-rectangle-white';
                    } else {
                      this.mode = IdCaptureMode.DETECTING_STRAIGHT_ID;
                    }
                  }
                }, 1000);

                return; // Exit this frame processing, as countdown takes over
              } else {
                LoggerService.logMulti('Looking for straight ID... Please align your ID with the guide.');
              }
            } else if (this.mode === IdCaptureMode.DETECTING_TILT_LIVE) {
              // In tilt mode, just show general rectangle and its aspect ratio as "tilt" info
              if (detectedObject.width >= this.detectedWidth * 0.9 && detectedObject.height < this.detectedHeight * 0.7) {
                this.guideColor = 'id-guide-rectangle-green';
                this.titleMesage = this.translations.IdCaptureValues.Waiting;
                LoggerService.logMulti(`ID appears tilted (Aspect Ratio: ${detectedObject.aspectRatio.toFixed(2)}).`);
                this.mode = IdCaptureMode.TILT_DETECTED;
                this.stopRecording();
              } else {
                this.guideColor = 'id-guide-rectangle-yellow';
                LoggerService.logMulti(`ID is relatively straight (Aspect Ratio: ${detectedObject.aspectRatio.toFixed(2)}).`);
                this.titleMesage = this.translations.IdCaptureValues.NoTiltDetected;
              }
              LoggerService.logMulti('Detecting tilt... Observe the highlighted object.');
            }
          } else {
            // No object detected
            this.detectedArea = null;
            if (this.mode === IdCaptureMode.DETECTING_STRAIGHT_ID) {
              LoggerService.logMulti('No ID detected. Please ensure ID is visible.');
            } else if (this.mode === IdCaptureMode.DETECTING_TILT_LIVE) {
              LoggerService.logMulti('No object detected for tilt analysis.');
            }
          }
        } catch (error) {
          LoggerService.error('TensorFlow.js processing error:', error);
          LoggerService.logMulti('AI processing error. See console for details.');
          this.mode = IdCaptureMode.IDLE;
          this.detectedArea = null;
          if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
          }
        }
      }

      // --- Schedule Next Frame (ONLY if actively detecting in a continuous mode) ---
      if (this.mode === IdCaptureMode.DETECTING_STRAIGHT_ID || this.mode === IdCaptureMode.DETECTING_TILT_LIVE) {
        await delay(50);
        this.animationFrameId = requestAnimationFrame(async () => await this.processVideoFrame());
      } else {
        if (this.animationFrameId !== null) {
          cancelAnimationFrame(this.animationFrameId);
          this.animationFrameId = null;
        }
      }
    }
  }

  private applyConditionsForDetection(detectedObject: DetectedObject, video: HTMLVideoElement): boolean {
    let aspectRatioPassed = detectedObject.aspectRatio > 1.3 && detectedObject.aspectRatio < 1.8;
    let areaPassed = detectedObject.objectArea > video.videoWidth * video.videoHeight * 0.4;
    let centerPassed =
      detectedObject.x <= video.videoWidth * 0.3 &&
      detectedObject.y <= video.videoHeight * 0.3 &&
      detectedObject.x >= video.videoWidth * 0.1 &&
      detectedObject.y >= video.videoHeight * 0.1;
    return aspectRatioPassed && areaPassed && centerPassed;
  }

  render() {
    let cameraVideoClass = 'cameraVideo';

    if (store.device.device.isDesktop) {
      cameraVideoClass = 'cameraVideoSelfieDesk';
    }

    return (
      <div class="container flex center">
        <div class="px-1 w-100">
          <h1 class={this.titleStyle} innerHTML={this.titleMesage}></h1>

          <div class="w-100 h-100 rounded" ref={el => (this.videoContainer = el)}>
            <div class="camera rounded">
              {this.isLoading && (
                <div class="loader-overlay">
                  <div class="loader-spinner"></div>
                </div>
              )}
              <video id="video" loop autoplay playsinline muted class={cameraVideoClass} ref={el => (this.videoElement = el)} />
              <canvas id="output" ref={el => (this.canvasElement = el)} style={{ display: 'none' }} />
              <canvas id="processingCanvasElement" ref={el => (this.processingCanvasElement = el)} style={{ display: 'none' }} />
              <div class={this.guideColor}></div>
              {this.isCapturing && <div class="capture-flash-overlay"></div>}
              {this.countdown > 0 && (
                <div class="countdown-overlay-style">
                  <span class="countdown-text-style">{this.countdown}</span>
                </div>
              )}
            </div>
          </div>
          {store.manualCaptureEnabled && (
            <div class="flex center">
              <div class="take-photo flex center mt-2 w-100 mx-2" onClick={() => this.manualCapture()}>
                {this.manualCapturing ? <img class="loading-image loading-image-button" src={loaderImg}></img> : <img class="h-100 d-block" src={icoPhoto}></img>}
              </div>
            </div>
          )}
          {store.debug == true && (
            <div class="text-center">
              <p>{store.requestId}</p>
            </div>
          )}
          <div class="footer text-center">
            <img src={logoOntrace}></img>
          </div>
        </div>
      </div>
    );
  }
}
